import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useChartOfAccounts,
  useCreateCampaign,
  useFinanceSettingsStore,
  useUpdateCampaign
} from "features/finances";
import moment from "moment";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  flex: 1;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  flex: 1;
  background-color: transparent;
`;

const campaignSchema = yup.object({
  campaign_name: yup.string().required("Campaign name is required"),
  purpose: yup.string().required("Purpose is required"),
  goal_amount: yup
    .number()
    .typeError("Goal amount must be a number")
    .required("Goal amount is required"),
  start_date: yup.string().required("Start date is required"),
  end_date: yup.string().required("End date is required")
});

export function CampaignModal({ isOpen, onClose, data }) {
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [{ column: "account_type", operator: "in", value: ["INCOME"] }]
  });
  const openCreateChartOfAccountModal = useFinanceSettingsStore(
    state => state.openCreateChartOfAccountModal
  );
  const setReturnToCampaign = useFinanceSettingsStore(
    state => state.setReturnToCampaign
  );

  const createCampaignMutation = useCreateCampaign();
  const updateCampaignMutation = useUpdateCampaign();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      campaign_name: "",
      chart_of_account_id: "",
      purpose: "",
      goal_amount: 0,
      start_date: "",
      end_date: ""
    },
    resolver: yupResolver(campaignSchema)
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        goal_amount: Number(data.goal_amount) || 0,
        start_date: moment(data.start_date).format("YYYY-MM-DD"),
        end_date: moment(data.end_date).format("YYYY-MM-DD")
      });
    }
  }, [data]);

  const onSubmit = values => {
    const campaignData = {
      chart_of_account_id: values.chart_of_account_id ?? "",
      campaign_name: values.campaign_name ?? "",
      purpose: values.purpose ?? "",
      goal_amount: values.goal_amount ?? 0,
      start_date: values.start_date ?? "",
      end_date: values.end_date ?? ""
    };

    if (data) {
      updateCampaignMutation.mutate(
        {
          churchId: selectedChurch?.id,
          campaignId: data.id,
          campaignData
        },
        {
          onSuccess: () => {
            reset();
            onClose();
          }
        }
      );
    } else {
      createCampaignMutation.mutate(
        {
          churchId: selectedChurch?.id,
          campaignData
        },
        {
          onSuccess: () => {
            reset();
            onClose();
          }
        }
      );
    }
  };

  const handleOpenChartOfAccount = () => {
    setReturnToCampaign(true);
    onClose();
    openCreateChartOfAccountModal();
  };

  return (
    <BaseModal
      title={data ? "Edit Campaign" : "Create Campaign"}
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      footer={
        <Stack direction="row" spacing={2}>
          <Button
            flavor="outlined"
            size="small"
            style={{
              flex: 1,
              color: "#000",
              borderColor: "#000",
              borderWidth: 1
            }}
            onClick={onClose}
            disabled={
              createCampaignMutation.isLoading ||
              updateCampaignMutation.isLoading
            }
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmit)}
            style={{ flex: 1 }}
            disabled={
              createCampaignMutation.isLoading ||
              updateCampaignMutation.isLoading
            }
          >
            {data ? "Save changes" : "Create campaign"}
          </Button>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="campaign_name">Campaign Name</Label>
        <Input id="campaign_name" {...register("campaign_name")} />
        <ErrorMessage
          errors={errors}
          name="campaign_name"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="purpose">Purpose</Label>
        <Input id="purpose" {...register("purpose")} />
        <ErrorMessage
          errors={errors}
          name="purpose"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="chart_of_account_id">Chart of account</Label>
        <Select
          id="chart_of_account_id"
          {...register("chart_of_account_id")}
          onChange={e => {
            if (e.target.value === "create") {
              handleOpenChartOfAccount();
              setValue("chart_of_account_id", "");
            } else {
              setValue("chart_of_account_id", e.target.value);
            }
          }}
        >
          <option value="" disabled>
            Select chart of account
          </option>
          <option value="create">Create chart of account</option>
          {chartOfAccountsQuery.data?.result?.map(chartOfAccount => (
            <option key={chartOfAccount.id} value={chartOfAccount.id}>
              {chartOfAccount.account_name}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="chart_of_account_id"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="goal_amount">Goal in amount</Label>
        <Controller
          name="goal_amount"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="goal_amount"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="goal_amount"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="start_date">Start date</Label>
        <EthiopianDateField
          size="small"
          value={startDate}
          onChange={e => setValue("start_date", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="start_date"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="end_date">End date</Label>
        <EthiopianDateField
          size="small"
          value={endDate}
          onChange={e => setValue("end_date", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="end_date"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError
        error={createCampaignMutation.error || updateCampaignMutation.error}
        sx={{ mt: 2 }}
      />
    </BaseModal>
  );
}
